import type { ReactNode } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { type RefObject, useLayoutEffect, useMemo } from "react";

import type { MainPanelConfig } from "../store/panels.store.types";

import { calculateTopPanelSize } from "../components/PanelsView.helpers";

interface UseVerticalPanelPairResizeProps {
    topConfig: MainPanelConfig;
    bottomConfig: MainPanelConfig;
    topASlot?: ReactNode;
    topBSlot?: ReactNode;
    bottomASlot?: ReactNode;
    bottomBSlot?: ReactNode;
    lastTopPanelSize: number | undefined;
    topPanelRef: RefObject<ImperativePanelHandle | null>;
    bottomPanelRef: RefObject<ImperativePanelHandle | null>;
}

/**
 * Resizes a vertical pair of panels (top + bottom) so each can minimize/expand independently.
 * Bottom is applied first so that when both are minimized, the top collapses correctly.
 * Returns whether the bottom is minimized/closed for maxHeight so it stays header-only.
 */
export function useVerticalPanelPairResize({
    topConfig,
    bottomConfig,
    topASlot,
    topBSlot,
    bottomASlot,
    bottomBSlot,
    lastTopPanelSize,
    topPanelRef,
    bottomPanelRef,
}: UseVerticalPanelPairResizeProps) {
    const topSize = useMemo(
        () =>
            calculateTopPanelSize({
                lastSize: lastTopPanelSize,
                topConfig,
                bottomConfig,
                topASlot,
                topBSlot,
                bottomASlot,
                bottomBSlot,
            }),
        [lastTopPanelSize, topConfig, bottomConfig, topASlot, topBSlot, bottomASlot, bottomBSlot],
    );

    const isBottomMinimizedOrClosed =
        (bottomConfig.selectedTab === "a" ? bottomConfig.a.isMinimized : bottomConfig.b.isMinimized) ||
        (bottomConfig.a.isClosed && bottomConfig.b.isClosed);

    const bottomSize = isBottomMinimizedOrClosed ? 0 : 100 - topSize;

    useLayoutEffect(() => {
        bottomPanelRef.current?.resize(bottomSize);
        topPanelRef.current?.resize(topSize);
        // oxlint-disable-next-line exhaustive-deps -- refs are stable, only resize when size/config changes
    }, [topSize, bottomSize, topConfig, bottomConfig]);

    return { isBottomMinimizedOrClosed };
}
